import { useCallback } from 'react'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'

import {
  canvasRefAtom,
  pageExceedsCanvasAtom,
  pageRefAtom,
  pageZoomAtom,
} from '@/atoms/editorAtoms'
import { EDITOR_CANVAS_PAGE_TOP_PX } from '@/components/page/Items/EditorTextBoxToolbar.geometry'

const MIN_ZOOM = 10
const MAX_ZOOM = 400
const DEFAULT_ZOOM_STEP = 10
const FIT_TO_VIEW_HORIZONTAL_PADDING = 40 // pixels of padding on left/right
// The fitted page spans from its offset down the canvas (room for a two-row
// text-box toolbar above it) to the top edge of the sticky editor toolbar
// (48px tall, 24px up from the bottom).
const FIT_TO_VIEW_TOP_PADDING = EDITOR_CANVAS_PAGE_TOP_PX
const FIT_TO_VIEW_BOTTOM_PADDING = 72

/**
 * Hook for managing page zoom in the editor.
 * Returns methods for zooming in/out and fitting the page to the viewport.
 *
 * @param {number} zoomStep - The percentage to increment/decrement zoom (default: 10)
 * @returns {{ zoomIn: Function, zoomOut: Function, fitToView: Function, zoom: number }}
 */
export const useZoom = (zoomStep = DEFAULT_ZOOM_STEP) => {
  const [zoom, setZoom] = useRecoilState(pageZoomAtom)
  const setPageExceedsCanvas = useSetRecoilState(pageExceedsCanvasAtom)
  const pageRef = useRecoilValue(pageRefAtom)
  const canvasRef = useRecoilValue(canvasRefAtom)

  /**
   * Updates zoom and pageExceedsCanvas state
   * @param {number} newZoom - The new zoom level
   * @param {Object} options - Optional settings
   * @param {number|null} options.newPageWidth - Calculate pageExceedsCanvas from this width
   * @param {boolean|null} options.pageExceedsCanvas - Explicitly set pageExceedsCanvas value
   */
  const updateZoom = useCallback(
    (newZoom, { newPageWidth = null, pageExceedsCanvas = null } = {}) => {
      const clampedZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, newZoom))
      setZoom(clampedZoom)

      if (pageExceedsCanvas !== null) {
        setPageExceedsCanvas(pageExceedsCanvas)
      } else if (canvasRef && newPageWidth !== null) {
        const canvasWidth = canvasRef.getBoundingClientRect().width
        setPageExceedsCanvas(newPageWidth > canvasWidth)
      }
    },
    [canvasRef, setZoom, setPageExceedsCanvas],
  )

  /**
   * Zoom in by zoomStep percentage
   */
  const zoomIn = useCallback(() => {
    if (zoom >= MAX_ZOOM) return

    if (pageRef && canvasRef) {
      const pageWidth = pageRef.getBoundingClientRect().width
      const newPageWidth = pageWidth * (1 + zoomStep / 100)
      updateZoom(zoom + zoomStep, { newPageWidth })
    } else {
      updateZoom(zoom + zoomStep)
    }
  }, [zoom, zoomStep, pageRef, canvasRef, updateZoom])

  /**
   * Zoom out by zoomStep percentage
   */
  const zoomOut = useCallback(() => {
    if (zoom <= MIN_ZOOM) return

    if (pageRef && canvasRef) {
      const pageWidth = pageRef.getBoundingClientRect().width
      const newPageWidth = pageWidth * (1 - zoomStep / 100)
      updateZoom(zoom - zoomStep, { newPageWidth })
    } else {
      updateZoom(zoom - zoomStep)
    }
  }, [zoom, zoomStep, pageRef, canvasRef, updateZoom])

  /**
   * Fit the page to the viewport with padding.
   * Calculates optimal zoom based on canvas and page dimensions.
   * Uses requestAnimationFrame to ensure layout is complete before measuring.
   *
   * @param {{ pageWidth: number, pageHeight: number, canvasElement?: HTMLElement }} options
   */
  const fitToView = useCallback(
    ({ pageWidth, pageHeight, canvasElement }) => {
      const canvas = canvasElement || canvasRef
      if (!canvas || !pageWidth || !pageHeight) return

      // Use requestAnimationFrame to ensure layout is complete
      requestAnimationFrame(() => {
        const canvasRect = canvas.getBoundingClientRect()

        // Ensure we have valid dimensions (canvas might not be laid out yet)
        if (canvasRect.width === 0 || canvasRect.height === 0) return

        // Available space: the page offset at the top, the sticky toolbar at the bottom
        const availableWidth = canvasRect.width - FIT_TO_VIEW_HORIZONTAL_PADDING
        const availableHeight =
          canvasRect.height -
          FIT_TO_VIEW_TOP_PADDING -
          FIT_TO_VIEW_BOTTOM_PADDING

        // Calculate zoom to fit each dimension
        const widthZoom = (availableWidth / pageWidth) * 100
        const heightZoom = (availableHeight / pageHeight) * 100

        // Use the smaller zoom to ensure page fits both dimensions
        const optimalZoom = Math.min(widthZoom, heightZoom)
        const roundedZoom = Math.round(optimalZoom)

        updateZoom(roundedZoom, { pageExceedsCanvas: false })

        // Reset scroll position to center the page
        canvas.scrollLeft = 0
        canvas.scrollTop = 0
      })
    },
    [canvasRef, updateZoom],
  )

  return {
    zoomIn,
    zoomOut,
    fitToView,
    zoom,
  }
}
